import { ArrowDown, ArrowUp } from "@procore/core-icons";
import { Box, Button, colors, spacing, Table, Typography } from "@procore/core-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDbStatus } from "hooks/useDbStatus";
import { getPropertyDisplayName } from "utils/objectData";

import { type PropertyGroup } from "../hooks/usePropertyFiltering";
import { PropertyRow } from "./PropertyRow";

interface PropertyTableProps {
    group: PropertyGroup;
    searchTerm: string;
    isFullySelected: boolean;
    isPartiallySelected: boolean;
    isPropertySelected: (groupName: string, propertyName: string, propertyValue: string) => boolean;
    onToggleGroupSelection: () => void;
    onTogglePropertySelection: (groupName: string, propertyName: string, propertyValue: string) => void;
}

export function PropertyTable({
    group,
    searchTerm,
    isFullySelected,
    isPartiallySelected,
    isPropertySelected,
    onToggleGroupSelection,
    onTogglePropertySelection,
}: PropertyTableProps) {
    const { t } = useTranslation();
    const dbStatus = useDbStatus();
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    const sortedProperties = useMemo(() => {
        const sorted = [...group.properties].sort(([a], [b]) => {
            return getPropertyDisplayName(a).localeCompare(getPropertyDisplayName(b), "en", { sensitivity: "accent" });
        });
        return sortDirection === "desc" ? sorted.reverse() : sorted;
    }, [group.properties, sortDirection]);

    const toggleSortDirection = () => {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    };

    return (
        <Table.Container style={{ width: "100%" }}>
            <Table style={{ tableLayout: "fixed", width: "100%" }}>
                <Table.Header>
                    <Table.HeaderRow>
                        <Table.HeaderCell>
                            <Box display="flex" alignItems="center">
                                <Table.Checkbox
                                    style={{ paddingRight: spacing.sm }}
                                    checked={isFullySelected}
                                    indeterminate={isPartiallySelected}
                                    onChange={onToggleGroupSelection}
                                    disabled={!dbStatus.isIndexAvailable}
                                />
                                <Typography
                                    style={{ paddingRight: spacing.xs }}
                                    intent="small"
                                    weight="semibold"
                                    color="gray30"
                                >
                                    {t("name")}
                                </Typography>
                                <Button
                                    variant="tertiary"
                                    icon={
                                        sortDirection === "asc" ? (
                                            <ArrowDown color={colors.blue50} />
                                        ) : (
                                            <ArrowUp color={colors.blue50} />
                                        )
                                    }
                                    size="sm"
                                    onClick={toggleSortDirection}
                                />
                            </Box>
                        </Table.HeaderCell>
                        <Table.HeaderCell>{t("value")}</Table.HeaderCell>
                    </Table.HeaderRow>
                </Table.Header>
                <Table.Body>
                    {sortedProperties.map(([propertyName, propertyValue]) => (
                        <PropertyRow
                            key={`${propertyName}-${propertyValue}`}
                            propertyName={propertyName}
                            propertyValue={propertyValue}
                            searchTerm={searchTerm}
                            isSelected={isPropertySelected(group.name, propertyName, propertyValue)}
                            onToggleSelection={() => onTogglePropertySelection(group.name, propertyName, propertyValue)}
                        />
                    ))}
                </Table.Body>
            </Table>
        </Table.Container>
    );
}
